import {
  formatearCLP,
  formatearPorcentajeSimple,
  type PeriodoDashboard,
} from '@lexicore/shared';
import { Cargando, ErrorCarga } from '../../componentes/UI';
import { IconoCerrar, IconoDescargar, IconoImprimir } from '../../componentes/Iconos';
import { useInformeEjecutivo } from '../../lib/consultas';

interface ModalInformeEjecutivoProps {
  abierto: boolean;
  alCerrar: () => void;
  periodo: PeriodoDashboard;
  desde?: string;
  hasta?: string;
}

export function ModalInformeEjecutivo({
  abierto,
  alCerrar,
  periodo,
  desde,
  hasta,
}: ModalInformeEjecutivoProps) {
  const { data, isLoading, isError, error, refetch } = useInformeEjecutivo({
    periodo,
    desde,
    hasta,
  });

  if (!abierto) return null;

  const handleDescargarCSV = () => {
    const params = new URLSearchParams();
    if (periodo) params.set('periodo', periodo);
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    window.open(`/api/analitica/informe-ejecutivo.csv?${params.toString()}`, '_blank');
  };

  const handleImprimir = () => {
    window.print();
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div
        className="modal"
        style={{ maxWidth: 840, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera del modal */}
        <div
          className="modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📑</span>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Informe Ejecutivo de Gestión y Directorio
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={alCerrar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: 4,
            }}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={18} />
          </button>
        </div>

        {/* Cuerpo del modal */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {isLoading && <Cargando texto="Generando Informe Ejecutivo Consolidado…" />}
          
          {isError && (
            <ErrorCarga
              error={error}
              reintentar={() => void refetch()}
            />
          )}

          {data && (
            <>
              {/* ── ENCABEZADO CORPORATIVO FORMAL ── */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 16,
                  padding: '16px 20px',
                  background: 'var(--bg-muted, rgba(255,255,255,0.03))',
                  borderRadius: 8,
                  border: '1px solid var(--border-color)',
                }}
              >
                <div>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 4,
                      background: 'var(--accent-blue-soft, rgba(37,99,235,0.15))',
                      color: 'var(--accent-blue, #3b82f6)',
                      marginBottom: 6,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Documento Ejecutivo Oficial
                  </span>
                  <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                    {data.empresa.nombre}
                  </h3>
                  <p style={{ fontSize: 12, margin: '4px 0 0', color: 'var(--text-secondary)' }}>
                    Período Analizado: <strong>{data.empresa.periodoAnalizado}</strong>
                  </p>
                </div>

                <div style={{ textAlign: 'right', fontSize: 11.5, color: 'var(--text-muted)' }}>
                  <div>Fecha de Emisión:</div>
                  <strong style={{ color: 'var(--text-primary)', fontSize: 12.5 }}>
                    {new Date(data.empresa.fechaGeneracion).toLocaleDateString('es-CL', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </strong>
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    Sistema CRM LEXICORE · Torre de Control
                  </div>
                </div>
              </div>

              {/* ── INDICADORES MAESTROS (NORTH STAR 10 KPIS) ── */}
              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', color: 'var(--text-primary)' }}>
                  🎯 Indicadores Maestros de Negocio (North Star)
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 10,
                  }}
                >
                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total Adjudicado</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-green, #10b981)', marginTop: 2 }}>
                      {formatearCLP(data.kpisMaestros.totalAdjudicado)}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Resultado Operacional</div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: data.kpisMaestros.resultadoOperacional >= 0 ? 'var(--accent-green, #10b981)' : 'var(--accent-red, #ef4444)',
                        marginTop: 2,
                      }}
                    >
                      {formatearCLP(data.kpisMaestros.resultadoOperacional)}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Margen Operativo</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
                      {data.kpisMaestros.margenOperativoPct !== null
                        ? formatearPorcentajeSimple(data.kpisMaestros.margenOperativoPct, 1)
                        : '—'}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Cartera por Cobrar</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-yellow, #f59e0b)', marginTop: 2 }}>
                      {formatearCLP(data.kpisMaestros.saldoPorCobrar)}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Saldo Vencido en Mora</div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: data.kpisMaestros.saldoVencido > 0 ? 'var(--accent-red, #ef4444)' : 'var(--text-primary)',
                        marginTop: 2,
                      }}
                    >
                      {formatearCLP(data.kpisMaestros.saldoVencido)}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Win Rate Mercado Público</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-purple, #8b5cf6)', marginTop: 2 }}>
                      {data.kpisMaestros.winRatePct !== null
                        ? formatearPorcentajeSimple(data.kpisMaestros.winRatePct)
                        : '—'}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Pipeline en Juego</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-blue, #3b82f6)', marginTop: 2 }}>
                      {formatearCLP(data.kpisMaestros.pipelineAbierto)}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Proyectos en Ejecución</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
                      {data.kpisMaestros.proyectosActivos} activos
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Días Cobro Estado (DSO)</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
                      {data.kpisMaestros.diasPromedioCobro !== null
                        ? `${Math.round(data.kpisMaestros.diasPromedioCobro)} días`
                        : '—'}
                    </div>
                  </div>

                  <div style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Gastos Estructura Fija</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
                      {formatearCLP(data.kpisMaestros.gastosGeneralesMes)}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── ANTIGÜEDAD DE LA CARTERA POR COBRAR (AGING) ── */}
              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', color: 'var(--text-primary)' }}>
                  ⏳ Antigüedad de Deuda por Cobrar al Estado (Aging)
                </h4>
                <div className="tabla-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Tramo de Vencimiento</th>
                        <th style={{ textAlign: 'center' }}>Documentos</th>
                        <th style={{ textAlign: 'right' }}>Monto Pendiente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.carteraTramos.map((tramo, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600 }}>{tramo.tramo}</td>
                          <td style={{ textAlign: 'center' }}>{tramo.documentos} facturas</td>
                          <td className="td-mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                            {formatearCLP(tramo.monto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── CONTINGENCIAS ACTIVAS EN RADAR DE FOCO ── */}
              {data.radarFoco.totalAlertas > 0 && (
                <div>
                  <h4 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', color: 'var(--accent-red, #ef4444)' }}>
                    🚨 Resumen de Contingencias Críticas ({data.radarFoco.totalAlertas} alertas)
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                    {data.radarFoco.licitacionesUrgentes.slice(0, 2).map((lic) => (
                      <div
                        key={lic.id}
                        style={{
                          padding: '6px 10px',
                          background: 'var(--bg-muted)',
                          borderRadius: 4,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>
                          ⏳ Licitación: <strong>{lic.codigoMercadoPublico}</strong> — {lic.organismo} ({formatearCLP(lic.montoOfertadoNeto)})
                        </span>
                        <span style={{ color: 'var(--accent-red)', fontWeight: 700 }}>
                          Cierra en {lic.horasRestantes}h
                        </span>
                      </div>
                    ))}

                    {data.radarFoco.proyectosEnRiesgo.slice(0, 2).map((p) => (
                      <div
                        key={p.id}
                        style={{
                          padding: '6px 10px',
                          background: 'var(--bg-muted)',
                          borderRadius: 4,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>
                          ⚠️ Proyecto: <strong>{p.codigo}</strong> — {p.nombre}
                        </span>
                        <span style={{ color: 'var(--accent-yellow)', fontWeight: 700 }}>
                          {p.consumoPct}% presupuesto ({formatearCLP(p.costoReal)})
                        </span>
                      </div>
                    ))}

                    {data.radarFoco.facturasMoraCritica.slice(0, 2).map((fac) => (
                      <div
                        key={fac.id}
                        style={{
                          padding: '6px 10px',
                          background: 'var(--bg-muted)',
                          borderRadius: 4,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>
                          🚨 Factura {fac.numero ?? ''} — {fac.organismo}
                        </span>
                        <span style={{ color: 'var(--accent-red)', fontWeight: 700 }}>
                          +{fac.diasVencido}d vencida · {formatearCLP(fac.montoSaldo)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── BOTONES DE ACCIÓN ── */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                  marginTop: 10,
                  paddingTop: 16,
                  borderTop: '1px solid var(--border-color)',
                }}
              >
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleImprimir}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <IconoImprimir size={14} />
                    <span>Imprimir / Guardar PDF</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleDescargarCSV}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <IconoDescargar size={14} />
                    <span>Descargar CSV (Excel)</span>
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={alCerrar}
                >
                  Cerrar Informe
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
